/**
 * KpiCard — one KPI pattern for today's summaries and for the future analytics
 * metrics (score, coverage, overlap…).
 *
 * Rules from /docs/design.md §11: KPI are NEUTRAL by default. `status` is only
 * for a value that genuinely carries a state, and it is always paired with a
 * word or an icon — never colour alone. A value that cannot be computed says so
 * (A7) instead of rendering a zero.
 */
import { TrendingDown, TrendingUp } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { NotCalculable } from "@/components/system/states";
import { MetricExplainer } from "@/components/system/MetricExplainer";

const STATUS = {
  neutral: "text-content",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
  info: "text-info",
};

export function KpiCard({
  label,
  value,
  unit,
  delta,                  // { value: "+1,2 pt", direction: "up"|"down", label?: string }
  status = "neutral",
  statusLabel,            // the word that makes the status non-colour-only
  explain,                // { name, what, how?, formula?, coverage? }
  unavailable = false,
  unavailableReason,
  loading = false,
  density = "default",
  className = "",
  testId,
}) {
  const pad = density === "compact" ? "px-3 py-2.5" : "px-4 py-3.5";
  const Trend = delta?.direction === "down" ? TrendingDown : TrendingUp;

  return (
    <div
      data-testid={testId}
      className={`rounded-card border border-line-soft bg-surface shadow-card ${pad} min-w-[9rem] ${className}`}
    >
      <div className="flex items-start gap-1">
        {/* Phase 6 — the label wraps instead of being cut at 390px. */}
        <p className="text-label uppercase text-content-muted break-words">{label}</p>
        {explain && <MetricExplainer {...explain} testId={testId ? `${testId}-explain` : undefined} />}
      </div>

      {loading ? (
        <Skeleton className="h-7 w-20 mt-2" />
      ) : unavailable ? (
        <div className="mt-2">
          <NotCalculable
            reason={unavailableReason}
            testId={testId ? `${testId}-unavailable` : undefined}
          />
        </div>
      ) : (
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mt-1.5">
          <p
            data-testid={testId ? `${testId}-value` : undefined}
            className={`text-2xl font-semibold tabular ${STATUS[status] || STATUS.neutral}`}
          >
            {value}
            {unit && <span className="ml-1 text-sm font-medium text-content-muted">{unit}</span>}
          </p>
          {delta && (
            <span
              data-testid={testId ? `${testId}-delta` : undefined}
              className="inline-flex items-center gap-1 text-label text-content-secondary tabular"
            >
              <Trend className="w-3.5 h-3.5" aria-hidden="true" />
              {delta.value}
              {delta.label && <span className="text-content-muted">{delta.label}</span>}
            </span>
          )}
          {statusLabel && status !== "neutral" && (
            <span className={`text-label ${STATUS[status]}`}>{statusLabel}</span>
          )}
        </div>
      )}
    </div>
  );
}

export default KpiCard;
